/**
 * Les dashboards — déplacé depuis `pages/dashboard/api/` par #87, même raison que
 * `useDashboardKPIs` : #22 veut que le mobile s'alimente à ces hooks, et la
 * frontière de #23 lui interdit d'atteindre `src/pages/`. Extraction plutôt que
 * duplication, comme le prescrit le message du garde-fou.
 */
import { useQueryClient } from 'react-query';
import { useApiQuery, useApiMutation, ApiError } from '../useApi';
// `import type` et non un import de valeur : l'arête vers `pages/`
// disparaît alors du graphe que `mobile:check` suit (#23, #74).
import type { Dashboard } from '../../pages/dashboard/types';

// #################################################
//                      QUERIES
// #################################################

/**
 * Récupère la liste des dashboards de la facility
 */
export function useDashboards() {
    return useApiQuery<{ data: Dashboard[] }>('/dashboards', {}, {
        staleTime: 60000,
        refetchOnWindowFocus: false,
    });
}

/**
 * Récupère un dashboard par son ID avec ses widgets
 */
export function useDashboard(dashboardId: number | null) {
    const url = dashboardId ? `/dashboards/${dashboardId}` : '';

    return useApiQuery<Dashboard>(url, {}, {
        enabled: !!dashboardId,
        staleTime: 30000,
    });
}

// #################################################
//                      MUTATIONS
// #################################################

/**
 * Crée un nouveau dashboard
 */
export function useCreateDashboard(options: Record<string, unknown> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<Dashboard, ApiError, { name: string; template_id?: number }>(
        'post',
        () => '/dashboards',
        {
            ...options,
            onSuccess: (data: Dashboard) => {
                queryClient.invalidateQueries('/dashboards');
                (options.onSuccess as ((data: Dashboard) => void) | undefined)?.(data);
            },
        }
    );
}

/**
 * Met à jour un dashboard (renommer, set default, etc.)
 */
export function useUpdateDashboard(options: Record<string, unknown> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<Dashboard, ApiError, { id: number; name?: string; is_default?: boolean; sort_order?: number }>(
        'put',
        (variables) => `/dashboards/${variables.id}`,
        {
            ...options,
            onSuccess: (data: Dashboard) => {
                queryClient.invalidateQueries('/dashboards');
                (options.onSuccess as ((data: Dashboard) => void) | undefined)?.(data);
            },
        }
    );
}

/**
 * Supprime un dashboard
 */
export function useDeleteDashboard(options: Record<string, unknown> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<void, ApiError, { id: number }>(
        'delete',
        (variables) => `/dashboards/${variables.id}`,
        {
            ...options,
            onSuccess: () => {
                queryClient.invalidateQueries('/dashboards');
                (options.onSuccess as (() => void) | undefined)?.();
            },
        }
    );
}

/**
 * Sauvegarde le layout d'un dashboard (tous les widgets)
 */
export function useSaveDashboardLayout(options: Record<string, unknown> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<Dashboard, ApiError, { id: number; widgets: Array<{
        module_type: string;
        size_preset: string;
        grid_x: number;
        grid_y: number;
        grid_w: number;
        grid_h: number;
        config?: Record<string, unknown> | null;
    }> }>(
        'put',
        (variables) => `/dashboards/${variables.id}/layout`,
        {
            ...options,
            onSuccess: (data: Dashboard) => {
                queryClient.invalidateQueries('/dashboards');
                (options.onSuccess as ((data: Dashboard) => void) | undefined)?.(data);
            },
        }
    );
}

/**
 * Duplique un dashboard existant
 */
export function useDuplicateDashboard(options: Record<string, unknown> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<Dashboard, ApiError, { id: number; name?: string }>(
        'post',
        (variables) => `/dashboards/${variables.id}/duplicate`,
        {
            ...options,
            onSuccess: (data: Dashboard) => {
                queryClient.invalidateQueries('/dashboards');
                (options.onSuccess as ((data: Dashboard) => void) | undefined)?.(data);
            },
        }
    );
}

/**
 * Applique un template sur un dashboard existant (remplace les widgets)
 */
export function useApplyTemplate(options: Record<string, unknown> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<Dashboard, ApiError, { id: number; template_id: number }>(
        'post',
        (variables) => `/dashboards/${variables.id}/apply-template`,
        {
            ...options,
            onSuccess: (data: Dashboard) => {
                queryClient.invalidateQueries('/dashboards');
                (options.onSuccess as ((data: Dashboard) => void) | undefined)?.(data);
            },
        }
    );
}

/**
 * Crée un dashboard depuis un template
 */
export function useCreateDashboardFromTemplate(options: Record<string, unknown> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<Dashboard, ApiError, { template_id: number; name?: string }>(
        'post',
        () => '/dashboards/from-template',
        {
            ...options,
            onSuccess: (data: Dashboard) => {
                queryClient.invalidateQueries('/dashboards');
                (options.onSuccess as ((data: Dashboard) => void) | undefined)?.(data);
            },
        }
    );
}
